import { supabase } from '../lib/supabase';

/**
 * Service for managing dealership data with Supabase
 * Handles CRUD operations for dealerships table
 */

// Generate URL-safe slug from dealership name and city
export const generateDealershipSlug = (name, city) => {
  if (!name || !city) return '';
  
  const combined = `${name}-${city}`;
  
  // Convert to lowercase, replace umlauts, remove special chars, replace spaces with hyphens
  return combined
    ?.toLowerCase()
    ?.replace(/ä/g, 'ae')
    ?.replace(/ö/g, 'oe')
    ?.replace(/ü/g, 'ue')
    ?.replace(/ß/g, 'ss')
    ?.replace(/[^a-z0-9\s-]/g, '') // Remove special characters except spaces and hyphens
    ?.replace(/\s+/g, '-') // Replace spaces with hyphens
    ?.replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
    ?.trim();
};

// Convert snake_case from database to camelCase for React
const convertToCamelCase = (dealership) => {
  if (!dealership) return null;
  
  const converted = {
    id: dealership?.id,
    name: dealership?.name,
    city: dealership?.city,
    email: dealership?.email,
    contactEmail: dealership?.contact_email,
    phone: dealership?.phone,
    street: dealership?.street,
    postalCode: dealership?.postal_code,
    contactPerson: dealership?.contact_person,
    contactPhone: dealership?.contact_phone,
    website: dealership?.website,
    status: dealership?.status,
    notes: dealership?.notes,
    logo: dealership?.logo,
    logoAlt: dealership?.logo_alt,
    ciPrimaryColor: dealership?.ci_primary_color,
    ciSecondaryColor: dealership?.ci_secondary_color,
    logoBackgroundColor: dealership?.logo_background_color,
    verificationToken: dealership?.verification_token,
    tokenExpiresAt: dealership?.token_expires_at,
    urlSlug: dealership?.url_slug,
    voucherValue: dealership?.voucher_value,
    voucherCurrency: dealership?.voucher_currency,
    premium2026: dealership?.premium_2026 ?? 150,
    premium2027: dealership?.premium_2027 ?? 0,
    partnerCategoryId: dealership?.partner_category_id,
    showInPartnerFinder: dealership?.show_in_partner_finder,
    latitude: dealership?.latitude,
    longitude: dealership?.longitude,
    createdAt: dealership?.created_at,
    updatedAt: dealership?.updated_at,
  };
  
  // 🔍 DIAGNOSTIC LOGGING for color conversion
  console.group('🔄 Dealership Data Conversion');
  console.log('From Database (snake_case):', {
    ci_primary_color: dealership?.ci_primary_color,
    ci_secondary_color: dealership?.ci_secondary_color,
    logo_background_color: dealership?.logo_background_color
  });
  console.log('To React (camelCase):', {
    ciPrimaryColor: converted?.ciPrimaryColor,
    ciSecondaryColor: converted?.ciSecondaryColor,
    logoBackgroundColor: converted?.logoBackgroundColor
  });
  console.log('Colors Present:', !!(converted?.ciPrimaryColor || converted?.ciSecondaryColor));
  console.groupEnd();
  
  return converted;
};

// Convert camelCase from React to snake_case for database
const convertToSnakeCase = (dealership) => {
  if (!dealership) return null;
  
  const converted = {};
  
  // Only add fields that are explicitly present in the input
  if ('name' in dealership) converted.name = dealership?.name;
  if ('city' in dealership) converted.city = dealership?.city;
  if ('email' in dealership) converted.email = dealership?.email;
  if ('contactEmail' in dealership) converted.contact_email = dealership?.contactEmail;
  if ('phone' in dealership) converted.phone = dealership?.phone;
  if ('street' in dealership) converted.street = dealership?.street;
  if ('postalCode' in dealership) converted.postal_code = dealership?.postalCode;
  if ('contactPerson' in dealership) converted.contact_person = dealership?.contactPerson;
  if ('contactPhone' in dealership) converted.contact_phone = dealership?.contactPhone;
  if ('website' in dealership) converted.website = dealership?.website;
  if ('status' in dealership) converted.status = dealership?.status || 'active';
  if ('notes' in dealership) converted.notes = dealership?.notes;
  if ('logo' in dealership) converted.logo = dealership?.logo;
  if ('logoAlt' in dealership) converted.logo_alt = dealership?.logoAlt;
  if ('ciPrimaryColor' in dealership) converted.ci_primary_color = dealership?.ciPrimaryColor;
  if ('ciSecondaryColor' in dealership) converted.ci_secondary_color = dealership?.ciSecondaryColor;
  if ('logoBackgroundColor' in dealership) converted.logo_background_color = dealership?.logoBackgroundColor;
  if ('voucherValue' in dealership) converted.voucher_value = dealership?.voucherValue;
  if ('voucherCurrency' in dealership) converted.voucher_currency = dealership?.voucherCurrency;
  if ('premium2026' in dealership) converted.premium_2026 = dealership?.premium2026;
  if ('premium2027' in dealership) converted.premium_2027 = dealership?.premium2027;
  if ('partnerCategoryId' in dealership) converted.partner_category_id = dealership?.partnerCategoryId || null;
  if ('showInPartnerFinder' in dealership) converted.show_in_partner_finder = dealership?.showInPartnerFinder || false;
  if ('latitude' in dealership) converted.latitude = dealership?.latitude;
  if ('longitude' in dealership) converted.longitude = dealership?.longitude;
  
  // 🔍 DIAGNOSTIC LOGGING for color saving
  console.group('💾 Saving Dealership Colors to Database');
  console.log('From React (camelCase):', {
    ciPrimaryColor: dealership?.ciPrimaryColor,
    ciSecondaryColor: dealership?.ciSecondaryColor,
    logoBackgroundColor: dealership?.logoBackgroundColor
  });
  console.log('To Database (snake_case):', {
    ci_primary_color: converted?.ci_primary_color,
    ci_secondary_color: converted?.ci_secondary_color,
    logo_background_color: converted?.logo_background_color
  });
  console.log('Will Save Colors:', !!(converted?.ci_primary_color || converted?.ci_secondary_color));
  console.groupEnd();
  
  return converted;
};

// Generate verification token for dealership
export const generateVerificationToken = async (dealershipId) => {
  try {
    // First get the dealership to access its url_slug
    const { data: dealership, error: fetchError } = await supabase
      ?.from('dealerships')
      ?.select('url_slug')
      ?.eq('id', dealershipId)
      ?.single();

    if (fetchError) throw fetchError;

    const { data, error } = await supabase?.rpc('generate_dealership_verification_token', {
      dealership_id_param: dealershipId
    });

    if (error) throw error;

    // Generate proper verification URL using url_slug without /dealership/ prefix
    const origin = typeof window !== 'undefined' ? window.location?.origin : '';
    const verificationUrl = `${origin}/${dealership?.url_slug}/gutscheincheck`;

    return {
      success: true,
      token: data,
      verificationUrl: verificationUrl
    };
  } catch (error) {
    console.error('Error generating verification token:', error);
    return {
      success: false,
      error: error?.message || 'Failed to generate verification token'
    };
  }
};

// Get dealership by verification token
export const getDealershipByToken = async (token) => {
  try {
    const { data, error } = await supabase?.from('dealerships')?.select('*')?.eq('verification_token', token)?.single();

    if (error) throw error;

    // Check if token is expired
    if (data?.token_expires_at && new Date(data.token_expires_at) < new Date()) {
      return {
        success: false,
        error: 'Verification token has expired'
      };
    }

    return {
      success: true,
      dealership: data
    };
  } catch (error) {
    console.error('Error fetching dealership by token:', error);
    return {
      success: false,
      error: error?.message || 'Invalid verification token'
    };
  }
};

export const dealershipService = {
  /**
   * Get all dealerships from database
   * @returns {Promise<Array>} Array of dealership objects in camelCase
   */
  async getAll() {
    try {
      const { data, error } = await supabase?.from('dealerships')?.select('*')?.order('created_at', { ascending: false });

      if (error) throw error;

      return data?.map(convertToCamelCase) || [];
    } catch (error) {
      console.error('Error fetching dealerships:', error);
      throw new Error(error?.message || 'Failed to fetch dealerships');
    }
  },

  /**
   * Get a single dealership by ID
   * @param {string} id - Dealership ID
   * @returns {Promise<Object>} Dealership object in camelCase
   */
  async getById(id) {
    try {
      const { data, error } = await supabase?.from('dealerships')?.select('*')?.eq('id', id)?.single();

      if (error) throw error;

      return convertToCamelCase(data);
    } catch (error) {
      console.error('Error fetching dealership:', error);
      throw new Error(error?.message || 'Failed to fetch dealership');
    }
  },

  /**
   * Get a single dealership by slug (from url_slug column)
   * @param {string} slug - URL-safe dealership slug
   * @returns {Promise<Object>} Dealership object in camelCase
   */
  async getBySlug(slug) {
    try {
      console.log('🔍 Fetching dealership by slug:', slug);
      
      const { data, error } = await supabase?.from('dealerships')?.select('*')?.eq('url_slug', slug)?.eq('status', 'active')?.maybeSingle();

      if (error) throw error;
      
      if (!data) {
        throw new Error(`Dealership with slug "${slug}" not found or not active`);
      }
      
      // 🔍 DIAGNOSTIC: Log raw database response
      console.group('📊 Raw Database Response for Slug:', slug);
      console.log('Raw Data:', data);
      console.log('ci_primary_color (DB):', data?.ci_primary_color);
      console.log('ci_secondary_color (DB):', data?.ci_secondary_color);
      console.groupEnd();

      return convertToCamelCase(data);
    } catch (error) {
      console.error('Error fetching dealership by slug:', error);
      throw new Error(error?.message || 'Failed to fetch dealership');
    }
  },

  /**
   * Create a new dealership
   * @param {Object} dealershipData - Dealership data in camelCase
   * @returns {Promise<Object>} Created dealership in camelCase
   */
  async create(dealershipData) {
    try {
      const snakeCaseData = convertToSnakeCase(dealershipData);
      
      const { data, error } = await supabase?.from('dealerships')?.insert([snakeCaseData])?.select()?.single();

      if (error) throw error;

      return convertToCamelCase(data);
    } catch (error) {
      console.error('Error creating dealership:', error);
      throw new Error(error?.message || 'Failed to create dealership');
    }
  },

  /**
   * Update an existing dealership
   * @param {string} id - Dealership ID
   * @param {Object} dealershipData - Updated dealership data in camelCase
   * @returns {Promise<Object>} Updated dealership in camelCase
   */
  async update(id, dealershipData) {
    try {
      // First check if the dealership exists and get current email, contact_email, and contact_phone
      const { data: existingData, error: fetchError } = await supabase
        ?.from('dealerships')
        ?.select('id, email, contact_email, contact_phone')
        ?.eq('id', id)
        ?.single();

      if (fetchError) {
        // If the record doesn't exist or user doesn't have access to read it
        if (fetchError?.code === 'PGRST116') {
          throw new Error('Dealership not found or you do not have permission to update it');
        }
        throw fetchError;
      }

      // Normalize function for email and text fields
      const normalizeField = (value) => {
        if (value === null || value === undefined || value === '') return null;
        return value?.toString()?.trim()?.toLowerCase();
      };

      // Build a clean update object by only including fields that have actually changed
      const dataToUpdate = {};
      
      // Process each field in the incoming data
      for (const key of Object.keys(dealershipData)) {
        const value = dealershipData?.[key];
        
        // Special handling for email fields to prevent duplicate key violations
        if (key === 'email') {
          const newEmail = normalizeField(value);
          const existingEmail = normalizeField(existingData?.email);
          
          // Only include if actually different
          if (newEmail !== existingEmail && newEmail !== null) {
            // Check if this email is already taken by another dealership
            const { data: emailCheck, error: emailCheckError } = await supabase
              ?.from('dealerships')
              ?.select('id')
              ?.eq('email', value?.toString()?.trim())
              ?.neq('id', id)
              ?.maybeSingle();
            
            if (emailCheckError) {
              console.error('Error checking email uniqueness:', emailCheckError);
            }
            
            if (emailCheck) {
              throw new Error('Diese E-Mail-Adresse wird bereits von einem anderen Partner verwendet.');
            }
            
            dataToUpdate.email = value?.toString()?.trim(); // Use trimmed value
          }
        } else if (key === 'contactEmail') {
          const newContactEmail = normalizeField(value);
          const existingContactEmail = normalizeField(existingData?.contact_email);
          
          // Only include if actually different
          if (newContactEmail !== existingContactEmail) {
            dataToUpdate.contactEmail = value?.toString()?.trim() || null; // Use trimmed value or null
          }
        } else if (key === 'contactPhone') {
          const newContactPhone = normalizeField(value);
          const existingContactPhone = normalizeField(existingData?.contact_phone);
          
          // Only include if actually different
          if (newContactPhone !== existingContactPhone) {
            dataToUpdate.contactPhone = value?.toString()?.trim() || null; // Use trimmed value or null
          }
        } else {
          // For all other fields, include them as-is
          dataToUpdate[key] = value;
        }
      }

      // Convert to snake_case - only changed fields will be included
      const snakeCaseData = convertToSnakeCase(dataToUpdate);

      // If no fields to update, return existing data
      if (Object.keys(snakeCaseData)?.length === 0) {
        return convertToCamelCase(existingData);
      }

      // Perform the update
      const { data, error } = await supabase
        ?.from('dealerships')
        ?.update(snakeCaseData)
        ?.eq('id', id)
        ?.select();

      if (error) throw error;

      // Check if any rows were returned after update
      if (!data || data?.length === 0) {
        throw new Error('Failed to update dealership. No rows were modified. This may be due to RLS policies or the record no longer exists.');
      }

      return convertToCamelCase(data?.[0]);
    } catch (error) {
      console.error('Error updating dealership:', error);
      throw new Error(error?.message || 'Failed to update dealership');
    }
  },

  /**
   * Delete a dealership
   * @param {string} id - Dealership ID
   * @returns {Promise<boolean>} Success status
   */
  async delete(id) {
    try {
      const { error } = await supabase?.from('dealerships')?.delete()?.eq('id', id);

      if (error) throw error;

      return true;
    } catch (error) {
      console.error('Error deleting dealership:', error);
      throw new Error(error?.message || 'Failed to delete dealership');
    }
  },

  /**
   * Search dealerships by name, city, or email
   * @param {string} query - Search query
   * @returns {Promise<Array>} Array of matching dealerships in camelCase
   */
  async search(query) {
    try {
      const { data, error } = await supabase?.from('dealerships')?.select('*')?.or(`name.ilike.%${query}%,city.ilike.%${query}%,email.ilike.%${query}%`)?.order('created_at', { ascending: false });

      if (error) throw error;

      return data?.map(convertToCamelCase) || [];
    } catch (error) {
      console.error('Error searching dealerships:', error);
      throw new Error(error?.message || 'Failed to search dealerships');
    }
  },

  /**
   * Get dealerships by status
   * @param {string} status - Dealership status ('active' or 'inactive')
   * @returns {Promise<Array>} Array of dealerships with specified status
   */
  async getByStatus(status) {
    try {
      const { data, error } = await supabase?.from('dealerships')?.select('*')?.eq('status', status)?.order('created_at', { ascending: false });

      if (error) throw error;

      return data?.map(convertToCamelCase) || [];
    } catch (error) {
      console.error('Error fetching dealerships by status:', error);
      throw new Error(error?.message || 'Failed to fetch dealerships by status');
    }
  },
};